import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, Search, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiCall } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface Course {
    id: number;
    idformation: number;
    nomformation: string; // or name, depending on API mapping
    // Add other fields as needed
}

interface Member {
    id: number;
    learnybox_user_id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
    is_active: boolean;
    enrollment_date: string | null;
    last_activity_date: string | null;
}

const LearnyboxMembers: React.FC = () => {
    const { token } = useAuth();
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedCourseId, setSelectedCourseId] = useState<string>("");
    const [members, setMembers] = useState<Member[]>([]);
    const [isLoadingCourses, setIsLoadingCourses] = useState(false);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Fetch courses on mount
    useEffect(() => {
        const fetchCourses = async () => {
            setIsLoadingCourses(true);
            try {
                // The backend endpoint returns raw Learnybox API data
                const response: any = await apiCall(token, '/learnybox/courses');
                // The API returns [{idformation: ..., nomformation: ...}, ...], map it if necessary
                // Assuming response.data is the array
                // If apiCall returns {success, data}, use that
                let data = response;
                if (response.data) data = response.data;
                if (!Array.isArray(data) && Array.isArray(response)) data = response;

                setCourses(data);

                // Optional: Select first course by default
                if (data.length > 0) {
                    // Prefer ID or idformation? backend courseMembers uses course_id which matches idformation in import
                    const firstId = data[0].idformation || data[0].id;
                    if (firstId) setSelectedCourseId(String(firstId));
                }
            } catch (error) {
                console.error("Error fetching courses:", error);
                toast.error("Erreur lors du chargement des formations");
            } finally {
                setIsLoadingCourses(false);
            }
        };

        fetchCourses();
    }, []);

    // Fetch members when selectedCourseId changes
    useEffect(() => {
        if (!selectedCourseId || !token) return;

        const fetchMembers = async () => {
            setIsLoadingMembers(true);
            try {
                const response: any = await apiCall(token, `/learnybox/courses/${selectedCourseId}/members`);
                if (Array.isArray(response)) {
                    setMembers(response);
                }
            } catch (error) {
                console.error("Error fetching members:", error);
                toast.error("Erreur lors du chargement des membres");
            } finally {
                setIsLoadingMembers(false);
            }
        };

        fetchMembers();
    }, [selectedCourseId, token]);

    const filteredMembers = members.filter(member =>
        member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.first_name && member.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (member.last_name && member.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleRefresh = () => {
        if (selectedCourseId) {
            // In a real app with import command, we might trigger an import job here?
            // For now just refresh the local view
            const fetchMembers = async () => {
                setIsLoadingMembers(true);
                try {
                    const response: any = await apiCall(token, `/learnybox/courses/${selectedCourseId}/members`);
                    if (Array.isArray(response)) setMembers(response);
                    else if (response.data && Array.isArray(response.data)) setMembers(response.data);
                    else setMembers([]);
                    toast.success("Liste mise à jour");
                } catch (error) {
                    toast.error("Erreur");
                } finally {
                    setIsLoadingMembers(false);
                }
            };
            fetchMembers();
        }
    }

    return (
        <div className="container mx-auto py-8 px-4 space-y-8 animate-in fade-in duration-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 rounded-2xl">
                            <Users className="h-6 w-6 text-primary" />
                        </div>
                        <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">
                            Membres Learnybox
                            {selectedCourseId && <span className="ml-3 text-2xl text-slate-300">({filteredMembers.length})</span>}
                        </h1>
                    </div>
                    <p className="text-slate-500 font-medium ml-14">Consultez les inscrits par formation synchronisés depuis Learnybox.</p>
                </div>
            </div>

            <Card className="border-none shadow-2xl shadow-slate-200/40 rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-xl">
                <CardHeader className="p-8 pb-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex-1 max-w-xl">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Sélection de la formation</label>
                                {isLoadingCourses ? (
                                    <div className="flex items-center space-x-2 text-sm text-slate-500 h-12 border border-slate-100 rounded-2xl px-4 bg-slate-50/50">
                                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                        <span className="font-bold italic uppercase tracking-widest text-[10px]">Chargement des formations...</span>
                                    </div>
                                ) : (
                                    <Select value={selectedCourseId} onValueChange={setSelectedCourseId}>
                                        <SelectTrigger className="w-full h-12 rounded-2xl bg-slate-50 border-slate-100 focus:ring-primary/20 font-bold transition-all">
                                            <SelectValue placeholder="Sélectionner une formation" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-slate-100">
                                            {courses.map((course: any) => (
                                                <SelectItem key={course.id || course.idformation} value={String(course.idformation || course.id)} className="rounded-xl font-bold">
                                                    {course.nomformation || course.name || `Formation ${course.idformation}`}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 max-w-md">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Recherche</label>
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <Input
                                        placeholder="Email, nom..."
                                        className="pl-12 h-12 bg-slate-50 border-slate-100 rounded-2xl focus:ring-primary/20 font-medium"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-end h-full pt-6">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleRefresh}
                                className="h-12 w-12 rounded-2xl border-slate-100 bg-white shadow-xl shadow-slate-200/50 hover:bg-slate-50 transition-all hover:scale-110 active:scale-90"
                            >
                                <RefreshCw className={cn("h-4 w-4 text-primary", isLoadingMembers ? 'animate-spin' : '')} />
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-slate-50 hover:bg-transparent">
                                <TableHead className="w-[60px] pl-8 text-[11px] font-black uppercase tracking-widest text-slate-400">#</TableHead>
                                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Statut</TableHead>
                                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Collaborateur / Membre</TableHead>
                                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Identité Email</TableHead>
                                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Inscription</TableHead>
                                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Activité</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoadingMembers ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="h-64 text-center">
                                        <div className="flex flex-col items-center justify-center gap-3">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                            <p className="text-sm font-bold text-slate-400 italic uppercase tracking-widest">Chargement des membres...</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : filteredMembers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="h-64 text-center text-slate-400">
                                        <div className="flex flex-col items-center justify-center gap-4">
                                            <Users className="h-12 w-12 opacity-10" />
                                            <p className="text-sm font-bold italic uppercase tracking-widest">
                                                {searchTerm ? "Aucun membre trouvé" : "Formation vide"}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredMembers.map((member, index) => (
                                    <TableRow key={member.id} className="group border-slate-50 hover:bg-slate-50/50 transition-colors">
                                        <TableCell className="pl-8 py-5">
                                            <span className="text-xs font-black text-slate-300">
                                                {(index + 1).toString().padStart(2, '0')}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={cn(
                                                "text-[10px] font-semibold capitalize px-3 py-1 rounded-xl border-none shadow-sm",
                                                member.is_active ? "bg-emerald-50 text-emerald-600 shadow-emerald-200/50" : "bg-slate-100 text-slate-400"
                                            )}>
                                                {member.is_active ? "🔥 Actif" : "👤 Inactif"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors tracking-tight">
                                                    {member.first_name || ""} {member.last_name || ""}
                                                    {(!member.first_name && !member.last_name) && <span className="text-slate-300 italic">Anonyme</span>}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-sm font-bold text-slate-500">{member.email}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-xs font-bold text-slate-400 capitalize whitespace-nowrap">
                                                {member.enrollment_date ? new Date(member.enrollment_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : "-"}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                                                {member.last_activity_date ? new Date(member.last_activity_date).toLocaleDateString() : "-"}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default LearnyboxMembers;
